import React, { useState } from "react";
import { Box, Grid, Typography, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import colorConfigs from "../../../configs/colorConfigs";
import { typo } from "../../../assets/fonts/font";
import { ILocation } from "../../../models/ILocation";
import useSWR from "swr";
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import GoogleMapComponent from "./GoogleMapComponent";

const categories = ["Supermarket", "Convenience Store", "Retail", "Petrol Station"];

const LocationsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("Supermarket");
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);

  const { data: locations }: { data: ILocation[] | undefined } = useSWR(PATH_NAME.API_GET_LOCATIONS, generalFetcher);

  const filteredLocations = locations?.filter(location =>
    (location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.address.toLowerCase().includes(searchTerm.toLowerCase())) &&
    location.category.toLowerCase() === selectedCategory.toLowerCase()
  );

  const handleLocationClick = (location: ILocation) => {
    setSelectedLocation(location);
  };

  return (
    <Box sx={{ padding: "1rem", background: colorConfigs.secondary }}>
      <Grid container spacing={2}>
        {/* Sidebar for store locations */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              backgroundColor: colorConfigs.secondary,
              padding: "1rem",
              borderRadius: "8px",
              height: "calc(100vh - 200px)",
              overflowY: "auto",
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: "1rem", ...typo.title }}>
              Locations
            </Typography>
            <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
              <InputLabel
                sx={{
                  color: colorConfigs.primary,
                  fontFamily: typo.bodyContent.fontFamily,
                  "&.Mui-focused": {
                    color: colorConfigs.primary, // Ensure label color on focus
                  },
                }}
              >
                Category
              </InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                displayEmpty
                fullWidth
                size="small"
                label="Category"
                sx={{
                  fontFamily: typo.bodyContent.fontFamily,
                  fontSize: typo.bodyContent.fontSize,
                  color: typo.bodyContent.color,
                  "& .MuiSelect-outlined": {
                    borderColor: colorConfigs.primary,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colorConfigs.primary,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colorConfigs.primary,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colorConfigs.primary + " !important",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: colorConfigs.secondary,
                      color: typo.bodyContent.color,
                      fontFamily: typo.bodyContent.fontFamily,
                    },
                  },
                }}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category} sx={{ fontFamily: typo.bodyContent.fontFamily }}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Search Input */}
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              size="small"
              onChange={(e) => setSearchTerm(e.target.value)}
              InputLabelProps={{
                sx: {
                  ...typo.bodyContent,
                  borderColor: colorConfigs.primary,
                },
              }}
              InputProps={{
                inputProps: {
                  style: {
                    ...typo.bodyContent,
                    borderColor: colorConfigs.primary,
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: colorConfigs.primary,
                    ...typo.bodyContent,
                  },
                },
                "& .MuiInputLabel-root": {},
                "& .MuiInputLabel-root.Mui-focused": {
                  ...typo.bodyContent,
                  borderColor: colorConfigs.primary,
                },
              }}
            />

            {filteredLocations?.map((location, index) => (
              <Box
                key={index}
                sx={{
                  marginTop: "1rem",
                  marginBottom: "1rem",
                  cursor: "pointer",
                  background: colorConfigs.quaternary,
                  padding: 2,
                  border: "solid",
                  borderRadius: "10px",
                }}
                onClick={() => handleLocationClick(location)}
              >
                <Typography variant="h6" sx={{ ...typo.title }}>
                  {location.name}
                </Typography>
                <Typography variant="body2" sx={{ ...typo.bodyContent }}>
                  {location.address}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Google Map */}
        
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              height: "calc(100vh - 200px)",
              borderRadius: "8px",
              backgroundColor: "var(--color-disabled-background)",
              position: "relative",
            }}
          >
            <GoogleMapComponent locations={filteredLocations || []} selectedLocation={selectedLocation} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LocationsPage;
